<script lang="ts">
  import SelectInput from "@kontextwerk/web-sdk/ui/forms/SelectInput.svelte"
  import { tooltip } from "@kontextwerk/web-sdk/core/overlay/tooltip"
  import type {
    LoadListTableRow,
    UnitOptionPresentation,
  } from "../../../../_shared/_logic/load-list-row-model"

  const {
    row,
    disabled = false,
    onSelectionChange,
  }: {
    row: LoadListTableRow
    disabled?: boolean
    onSelectionChange: (row: LoadListTableRow, unitId: string | null) => void
  } = $props()
  const selected = $derived(
    row.unitOptions.find((option) => option.value === row.selectedUnitId) ?? null,
  )
  const byId = $derived(new Map(row.unitOptions.map((option) => [option.value, option])))
</script>

{#if row.status === "pending"}
  <span class="muted">Liegenschaft zuerst zuordnen</span>
{:else if !row.unitOptions.length}
  <span class="muted">Keine Einheiten hinterlegt</span>
{:else}
  <div class="batch-unit-cell">
    {#if row.unitCount === 1 && selected}
      <div class="unit-value">{@render unitLabel(selected)}</div>
    {:else}
      <SelectInput
        clearable={false}
        id={`batch-unit-select-${row.id}`}
        dataTestid={`batch-unit-select-${row.id}`}
        ariaLabel={`Einheit für ${row.address}, Zeile ${row.rowNumber}`}
        placeholder="Einheit wählen"
        value={row.selectedUnitId ?? ""}
        options={row.unitOptions}
        {disabled}
        surfaceTone="dark"
        classList="batch-unit-two-line-select"
        onValueChange={(value) => onSelectionChange(row, String(value || "") || null)}
        selectionRenderer={selectedValue}
        itemRenderer={optionValue}
      />
    {/if}
  </div>
{/if}

{#snippet unitLabel(option: UnitOptionPresentation)}
  <span class="unit-label">
    <span class="unit-label__headline">{option.headline}</span>
    <span class="unit-label__subline">{option.subline}</span>
  </span>
{/snippet}
{#snippet selectedValue({ selection })}
  {@const option = byId.get(String(selection?.value ?? ""))}
  {#if option}<span class="unit-value">{@render unitLabel(option)}</span>
  {:else}<span class="muted">Einheit wählen</span>{/if}
{/snippet}
{#snippet optionValue({ item })}
  {@const option = byId.get(String(item.value))}
  {#if option}
    <div
      class="unit-option"
      class:unavailable={!option.selectable}
      aria-disabled={!option.selectable}
      aria-label={option.disabledReason
        ? `${option.label}. ${option.disabledReason}`
        : option.label}
      use:tooltip={option.disabledReason ? { content: option.disabledReason, focus: true } : false}
    >
      {@render unitLabel(option)}
    </div>
  {/if}
{/snippet}

<style lang="less">
  .batch-unit-cell {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    min-height: 48px;
    display: flex;
    align-items: center;
  }
  .batch-unit-cell :global(.select-input-shell) {
    flex: 1;
    min-width: 0;
  }
  .batch-unit-cell :global(.batch-unit-two-line-select) {
    --height: 48px;
    --padding: 4px 0 !important;
    --selected-item-padding: 0 !important;
    --item-height: 48px;
    --item-line-height: 1.2;
  }
  .unit-value {
    display: block;
    min-width: 0;
    max-width: 100%;
    flex: 1;
  }
  .unit-label {
    display: flex;
    flex-direction: column;
    min-width: 0;
    width: 100%;
    gap: 0.15rem;
    line-height: 1.2;
  }
  .unit-label__headline,
  .unit-label__subline {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .unit-label__headline {
    font-weight: 700;
    font-size: 0.9rem;
  }
  .unit-label__subline {
    font-size: 0.76rem;
  }
  .unit-option {
    padding-block: 0.35rem;
  }
  .unavailable {
    opacity: 0.55;
  }
  .muted {
    font-size: 0.8rem;
    color: color-mix(in srgb, currentColor 70%, transparent);
  }
</style>
